import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { initialsOf } from "../utils/name.js";
import LocationPicker from "./LocationPicker.jsx";
import useDeliveryLocation from "../hooks/useDeliveryLocation.js";

export default function Header() {
  const { count } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const { loc, busy, error, detect } = useDeliveryLocation();

  const close = () => setOpen(false);
  const shopActive = pathname.startsWith("/products");
  const cartActive = pathname === "/cart";

  const doLogout = async () => {
    close();
    await logout();
    navigate("/");
  };

  return (
    <header className="hdr">
      <div className="hdr__inner">
        <Link to="/" className="hdr__logo" aria-label="RimmonFarm — Home" onClick={close}>
          Rimm<span className="hdr__logo-o">o</span>n<strong>Farm</strong>
        </Link>

        <LocationPicker loc={loc} busy={busy} error={error} onClick={detect} />

        {/* Nav links carry an inline glyph; the account/bag cluster stays as
            icon columns, so "browse" and "operate" read as different things.
            Signed-in customers lose the marketing links — those are for
            visitors still deciding. */}
        <nav className={`hdr__nav ${open ? "hdr__nav--open" : ""}`}>
          <NavLink to="/products" className={`hdr__link ${shopActive ? "active" : ""}`} onClick={close}>
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 7h16l-1 4a3 3 0 0 1-3 2H8a3 3 0 0 1-3-2z" />
              <path d="M6 13v7h12v-7" />
              <path d="M9 7V5a3 3 0 0 1 6 0v2" />
            </svg>
            Shop
          </NavLink>
          {user ? (
            <Link to="/account" className="hdr__link" onClick={close}>
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="m7 10 5 5 5-5" />
                <path d="M12 15V3" />
              </svg>
              My orders
            </Link>
          ) : (
            <>
              <a href="/#about" className="hdr__link" onClick={close}>
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M11 20A7 7 0 0 1 4 13V5a1 1 0 0 1 1-1h3a7 7 0 0 1 7 7v9" />
                  <path d="M11 20c0-4 2-7 6-9" />
                </svg>
                Our Story
              </a>
              <a href="/#reviews" className="hdr__link" onClick={close}>
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m12 3.5 2.6 5.3 5.9.9-4.2 4.1 1 5.8-5.3-2.8-5.3 2.8 1-5.8L3.5 9.7l5.9-.9z" />
                </svg>
                Reviews
              </a>
              <a href="/#contact" className="hdr__link" onClick={close}>
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15.5v3a2 2 0 0 1-2.2 2 19.5 19.5 0 0 1-8.5-3 19 19 0 0 1-6-6 19.5 19.5 0 0 1-3-8.6A2 2 0 0 1 3.3 1H6a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.9a2 2 0 0 1-.5 2.1L7.1 8.9a16 16 0 0 0 6 6l1.2-1.1a2 2 0 0 1 2.1-.5c.9.3 1.9.6 2.9.7a2 2 0 0 1 1.7 2z" />
                </svg>
                Contact
              </a>
            </>
          )}
        </nav>

        <span className="hdr__divider" aria-hidden="true" />

        <div className="hdr__end">
          {user ? (
            // Hover (or keyboard focus) reveals the account menu; the avatar
            // itself still links straight to /account for a plain click.
            <div className="hdr__usermenu">
              <Link to="/account" className="hdr__icon hdr__user" aria-label="My account" title={user.name} onClick={close}>
                <span className="hdr__avatar">{initialsOf(user.name)}</span>
                <span className="hdr__icon-label">Account</span>
              </Link>
              <div className="hdr__menu" role="menu">
                <div className="hdr__menu-head">
                  <strong>{user.name}</strong>
                  <small>{user.email}</small>
                </div>
                <Link to="/account" className="hdr__menu-item" role="menuitem" onClick={close}>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  My account
                </Link>
                <button className="hdr__menu-item hdr__menu-item--danger" role="menuitem" onClick={doLogout}>
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <path d="m16 17 5-5-5-5" />
                    <path d="M21 12H9" />
                  </svg>
                  Sign out
                </button>
              </div>
            </div>
          ) : (
            <Link to="/login" className="hdr__icon" aria-label="Sign in" onClick={close}>
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <span className="hdr__icon-label">Sign in</span>
            </Link>
          )}

          <Link to="/cart" className={`hdr__icon hdr__cart ${cartActive ? "hdr__cart--active" : ""}`} aria-label="View bag">
            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6z" />
              <path d="M3 6h18" />
              <path d="M16 10a4 4 0 0 1-8 0" />
            </svg>
            {count > 0 && <span className="hdr__cart-badge">{count}</span>}
            <span className="hdr__icon-label">Bag</span>
          </Link>

          <button
            className="hdr__toggle"
            aria-label="Menu"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            <span /><span /><span />
          </button>
        </div>
      </div>
    </header>
  );
}
